import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useThemeClasses } from '../hooks/useThemeClasses';
import { useApi } from '../hooks/useApi';
import { FiUser, FiLock, FiHome, FiArrowRight, FiArrowLeft } from 'react-icons/fi';
import { filterProductPlans } from '../utils/planUtils';
import { startSubscriptionCheckout } from '../utils/mercadopagoCheckout';
import { persistAuthSession, resolvePlanId } from '../utils/authSession';
import SignupStepIndicator from '../components/auth/SignupStepIndicator';
import PlanSelectionCards from '../components/auth/PlanSelectionCards';
import BusinessFiscalFields from '../components/auth/BusinessFiscalFields';
import { FormShell, FormSection, FormField, FormAlert, formInputClass } from '../components/form';
import {
  captureReferralFromSearch,
  getStoredReferralCode,
  clearStoredReferralCode,
} from '../utils/referral';
import { apiUrl } from '../config';
import { readApiJson } from '../utils/apiResponse';

const STEP_LABELS = ['Acceso y contraseña', 'Elegí tu plan', 'Datos de tu negocio'];
const STEP_LABELS_GOOGLE = ['Elegí tu plan', 'Datos de tu negocio'];

const CompletarPerfil = () => {
  const { user, loading: authLoading, refreshUser, token, refreshToken, login } = useContext(AuthContext);
  const tc = useThemeClasses();
  const { secureFetch } = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const [referralCode, setReferralCode] = useState('');

  const [step, setStep] = useState(1);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    password: '',
    confirmPassword: '',
    selectedPlan: null,
    companyData: {
      razonSocial: '',
      cuit: '',
      domicilio: '',
      pais: 'Argentina',
      localidad: '',
      email: user?.email || '',
      telefono: '',
    },
    fiscal: {
      regimenFiscal: '',
      categoriaMonotributo: '',
      actividad: 'servicios',
    },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const isGoogleUser = Boolean(user?.googleId || user?.avatar);

  // Guard: si terminó de cargar y no hay usuario autenticado, redirigir a login
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    if (!authLoading && user?.role === 'admin' && user?.isSuperAdmin === true) {
      navigate('/inicio');
    }
  }, [authLoading, user, navigate]);

  // Los usuarios de Google arrancan en el paso 2 (ya tienen acceso resuelto)
  useEffect(() => {
    if (user && isGoogleUser && step === 1) {
      setStep(2);
    }
  }, [user, isGoogleUser, step]);

  useEffect(() => {
    const code = captureReferralFromSearch(location.search) || getStoredReferralCode();
    if (code) setReferralCode(code);
  }, [location.search]);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const response = await secureFetch('/api/plans');
        if (response.ok) {
          const data = await readApiJson(response);
          setPlans(filterProductPlans(data));
        }
      } catch (err) {
        console.error('Error al cargar planes:', err);
      } finally {
        setLoadingPlans(false);
      }
    };
    loadPlans();
  }, [secureFetch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('company.')) {
      const field = name.replace('company.', '');
      setFormData((prev) => ({
        ...prev,
        companyData: { ...prev.companyData, [field]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validateStep1 = () => {
    if (!formData.name.trim()) {
      setError('El nombre completo es requerido');
      return false;
    }
    // Para usuarios de Google la contraseña es opcional
    if (!isGoogleUser) {
      if (formData.password.length < 6) {
        setError('La contraseña debe tener al menos 6 caracteres');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Las contraseñas no coinciden');
        return false;
      }
    } else if (formData.password && formData.password.length > 0) {
      // Si el usuario de Google ingresó contraseña igualmente la validamos
      if (formData.password.length < 6) {
        setError('La contraseña debe tener al menos 6 caracteres');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Las contraseñas no coinciden');
        return false;
      }
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.selectedPlan) {
      setError('Seleccioná un plan para continuar');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.companyData.razonSocial.trim()) {
      setError('El nombre de la empresa es requerido');
      return false;
    }
    const cuitNorm = String(formData.companyData.cuit || '').replace(/\D/g, '');
    if (!cuitNorm) {
      setError('El CUIT es requerido (Monotributo o RI)');
      return false;
    }
    if (cuitNorm.length !== 11) {
      setError('El CUIT debe tener 11 dígitos');
      return false;
    }
    if (!formData.companyData.domicilio.trim()) {
      setError('La dirección es requerida');
      return false;
    }
    if (!formData.fiscal.regimenFiscal) {
      setError('Indicá si sos Monotributo o Responsable inscripto');
      return false;
    }
    if (formData.fiscal.regimenFiscal === 'monotributo' && !formData.fiscal.categoriaMonotributo) {
      setError('Elegí la categoría de Monotributo');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    setError('');
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    } else if (step === 3 && validateStep3()) {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const payload = {
        name: formData.name || user?.name,
        // Solo enviar contraseña si el usuario la ingresó (Google users pueden omitirla)
        ...(formData.password ? { password: formData.password } : {}),
        planId: resolvePlanId(formData.selectedPlan),
        companyData: formData.companyData,
        fiscal: {
          ...formData.fiscal,
          categoriaMonotributo:
            formData.fiscal.regimenFiscal === 'monotributo'
              ? formData.fiscal.categoriaMonotributo
              : null,
        },
        ...(referralCode ? { referralCode } : {}),
      };

      const response = await secureFetch('/api/users/complete-profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        clearStoredReferralCode();
        const data = await readApiJson(response);
        const planId = resolvePlanId(formData.selectedPlan);
        const sessionToken = data.token || token;
        const sessionRefresh = data.refreshToken || refreshToken;
        const sessionUser = data.user || user;

        if (sessionToken && sessionUser) {
          login(sessionUser, sessionToken, sessionRefresh);
        }

        if (planId && sessionToken) {
          const request = (path, options = {}) =>
            fetch(apiUrl(path), {
              ...options,
              headers: {
                ...(options.headers || {}),
                Authorization: `Bearer ${sessionToken}`,
              },
            });

          try {
            await startSubscriptionCheckout({
              request,
              planId,
              billingCycle: 'monthly',
              source: 'signup',
              beforeRedirect: () => {
                persistAuthSession({
                  user: sessionUser,
                  token: sessionToken,
                  refreshToken: sessionRefresh,
                });
              },
            });
            return;
          } catch (checkoutErr) {
            setError(
              checkoutErr.message ||
                'Perfil guardado. Autorizá el plan en Mercado Pago desde Planes para activar la cuenta.'
            );
            navigate('/planes?pago=pendiente');
            return;
          }
        }
        await refreshUser();
        setSuccess(true);
        setTimeout(() => navigate('/inicio?onboarding=1'), 1000);
      } else {
        const data = await readApiJson(response);
        setError(data.message || 'Error al completar el perfil');
      }
    } catch (err) {
      setError(err.message || 'Error de conexión. Intentá nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = () => {
    if (step === 1) return 'Continuar';
    if (step === 2) return 'Continuar';
    return isGoogleUser ? 'Guardar e ir a Mercado Pago' : 'Ir a Mercado Pago';
  };

  const renderStep1 = () => (
    <FormSection
      title="Completá tu acceso"
      description={
        isGoogleUser
          ? 'Tu acceso ya está vinculado con Google. Podés crear una contraseña opcional para entrar sin Google.'
          : 'Confirmá tu nombre y definí una contraseña segura.'
      }
      section={tc.section}
      textPri={tc.textPri}
      textMuted={tc.textMuted}
    >
      <div className="space-y-4">
        <FormField label="Nombre completo" required labelClass={tc.labelClass}>
          <div className="relative">
            <FiUser className={`absolute left-3 top-3 h-4 w-4 ${tc.textMuted}`} />
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Ej: Juan Pérez"
              className={`${formInputClass(tc.inputClass)} pl-9`}
            />
          </div>
        </FormField>

        <FormField
          label={isGoogleUser ? 'Contraseña (opcional)' : 'Contraseña'}
          required={!isGoogleUser}
          hint={isGoogleUser ? 'Dejala en blanco si querés entrar solo con Google.' : 'Mínimo 6 caracteres'}
          labelClass={tc.labelClass}
          textMuted={tc.textMuted}
        >
          <div className="relative">
            <FiLock className={`absolute left-3 top-3 h-4 w-4 ${tc.textMuted}`} />
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required={!isGoogleUser}
              minLength={isGoogleUser ? undefined : 6}
              placeholder={isGoogleUser ? 'Opcional' : 'Tu contraseña'}
              className={`${formInputClass(tc.inputClass)} pl-9`}
            />
          </div>
        </FormField>

        {(!isGoogleUser || formData.password) && (
          <FormField label="Confirmar contraseña" required={!isGoogleUser} labelClass={tc.labelClass}>
            <div className="relative">
              <FiLock className={`absolute left-3 top-3 h-4 w-4 ${tc.textMuted}`} />
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required={!isGoogleUser}
                placeholder="Repetí tu contraseña"
                className={`${formInputClass(tc.inputClass)} pl-9`}
              />
            </div>
          </FormField>
        )}
      </div>
    </FormSection>
  );

  const renderStep2 = () => (
    <FormSection
      title="Elegí el plan para empezar"
      description="Al confirmar te llevamos a Mercado Pago para autorizar la suscripción. Podés pagar con la cuenta MP que uses; en Contsuite entrás con el email de tu registro."
      section={tc.section}
      textPri={tc.textPri}
      textMuted={tc.textMuted}
    >
      <PlanSelectionCards
        plans={plans}
        selectedPlan={formData.selectedPlan}
        onSelect={(plan) => setFormData((prev) => ({ ...prev, selectedPlan: plan }))}
        loading={loadingPlans}
        modoOscuro={tc.modoOscuro}
        signup
        compact
        textPri={tc.textPri}
        textSec={tc.textSec}
        textMuted={tc.textMuted}
      />
    </FormSection>
  );

  const renderStep3 = () => (
    <FormSection
      title="Datos de tu negocio"
      description="CUIT, dirección y si sos Monotributo o Responsable inscripto."
      section={tc.section}
      textPri={tc.textPri}
      textMuted={tc.textMuted}
    >
      <BusinessFiscalFields
        tc={tc}
        values={{
          companyName: formData.companyData.razonSocial,
          cuit: formData.companyData.cuit,
          address: formData.companyData.domicilio,
          regimenFiscal: formData.fiscal.regimenFiscal,
          categoriaMonotributo: formData.fiscal.categoriaMonotributo,
          actividad: formData.fiscal.actividad,
        }}
        onChange={(field, value) => {
          if (field === 'companyName') {
            setFormData((prev) => ({
              ...prev,
              companyData: { ...prev.companyData, razonSocial: value },
            }));
            return;
          }
          if (field === 'cuit') {
            setFormData((prev) => ({
              ...prev,
              companyData: { ...prev.companyData, cuit: value.replace(/\D/g, '') },
            }));
            return;
          }
          if (field === 'address') {
            setFormData((prev) => ({
              ...prev,
              companyData: { ...prev.companyData, domicilio: value },
            }));
            return;
          }
          setFormData((prev) => ({
            ...prev,
            fiscal: {
              ...prev.fiscal,
              [field]: value,
              ...(field === 'regimenFiscal' && value !== 'monotributo'
                ? { categoriaMonotributo: '' }
                : {}),
            },
          }));
        }}
      />
    </FormSection>
  );

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${tc.modoOscuro ? 'bg-black' : 'bg-[#f5f5f7]'}`}>
      <div className={`w-full ${step === 2 ? 'max-w-[1200px] mx-auto' : 'max-w-xl'}`}>
        <FormShell
          title="Activá tu cuenta"
          subtitle="Un último paso para registrar ventas, gastos y elegir tu plan."
          icon={<FiHome className="w-5 h-5" />}
          box={tc.box}
          divide={tc.divide}
          textPri={tc.textPri}
          textMuted={tc.textMuted}
        >
          <SignupStepIndicator
              step={isGoogleUser ? step - 1 : step}
              labels={isGoogleUser ? STEP_LABELS_GOOGLE : STEP_LABELS}
              modoOscuro={tc.modoOscuro}
            />

          {error && <FormAlert type="error" className="mb-4">{error}</FormAlert>}
          {success && (
            <FormAlert type="success" className="mb-4">Cuenta activada correctamente. Redirigiendo...</FormAlert>
          )}

          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}

          <div className={`pt-6 space-y-3 ${step > 1 ? '' : ''}`}>
            <button
              type="button"
              onClick={handleNext}
              disabled={loading || success}
              className={`w-full py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-colors ${tc.btnPrimary}`}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  {getButtonText()}
                  <FiArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            {/* No mostrar "Volver" al primer paso del flujo (en Google es paso 2) */}
            {step > 1 && !(isGoogleUser && step === 2) && (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                disabled={loading || success}
                className={`w-full py-2.5 rounded-xl font-semibold text-sm border flex items-center justify-center gap-2 ${tc.btnOutline}`}
              >
                <FiArrowLeft className="w-4 h-4" />
                Volver
              </button>
            )}
          </div>

          <p className={`text-center text-xs mt-4 ${tc.textMuted}`}>
            ¿Dudas sobre planes?{' '}
            <Link to="/planes" className="text-[#1F80FF] font-medium hover:underline">
              Ver comparación completa
            </Link>
          </p>
        </FormShell>
      </div>
    </div>
  );
};

export default CompletarPerfil;
